// ---- Live skill tunables ------------------------------------------------------
// THE merge point between the sim DEFAULTS (src/sim/*, the one home for the
// numbers' meaning + the values the parity tests pin) and the per-skill
// content/tunables/*.json docs (the values the studio Skill-Sim edits and the
// user "approves for the game"). Server handlers, the client HUD, and the
// studio drivers all read the EFFECTIVE values from here — never the sim
// constants directly — so a doc edit changes both sides on next boot with no
// code touch. The sim modules themselves never import this (purity rule:
// values reach the sim as arguments, exactly like catch tables).
//
// DOM/THREE/Bun-free: safe in the game client, the headless server, and the
// studio bundle alike. Adding a skill = a sibling block here + its doc fields
// in TunablesSchema (src/content/schemas.ts).

import { loadedTunables } from "./content/load.ts";
import {
  CAST_RANGE, CAST_CD,
  NOTICE_RADIUS, NOTICE_EVERY, INVESTIGATE_MIN, INVESTIGATE_MAX,
  NIBBLE_MIN, NIBBLE_MAX, NIBBLE_DUR, NIBBLE_GAP_MIN, NIBBLE_GAP_MAX,
  NIBBLE_SPOOK, PULL_SPOOK, BITE_WINDOW, SPOOK_CD, LOSE_CD, WARY_BUMP, WARY_DECAY,
  FISH_RESPAWN, SHADOW_DEPTH_OFFSET,
  REEL_ARROWS_BASE, REEL_ARROWS_PER_TIER, REEL_GAP_BASE, REEL_GAP_MIN, REEL_SPEED_RAMP,
  REEL_HIT_WINDOW, REEL_MISS_TENSION, REEL_HIT_RELIEF, REEL_INPUT_SLACK, REEL_DEADLINE_PAD,
  SOAK_TIME, NET_CAPACITY_MIN, NET_CAPACITY_MAX, NET_COUNT, NET_RANGE,
  ANGLING_XP, ANGLING_XP_DEFAULT, NETTING_XP_PER_FISH, FISHMONGER_PER_CATCH, FISHMONGER_PER_HAUL,
} from "./sim/fishing.ts";
import {
  TRAP_SOAK, TRAP_CAPACITY_MIN, TRAP_CAPACITY_MAX, TRAP_COUNT, TRAP_RANGE, TROPHY_TIER_MUL,
  STALKING_XP, STALKING_XP_DEFAULT, TRAPPING_XP_PER_GAME,
  HUNTSMAN_PER_TAKE, HUNTSMAN_PER_HAUL, HUNTSMAN_PER_TROPHY,
} from "./sim/hunting.ts";
import {
  HEARTH_RANGE, COOK_RATE_CD,
  COOK_XP, COOK_XP_DEFAULT, BREW_SOAK, BREW_CAPACITY_MIN, BREW_CAPACITY_MAX,
  BREW_COUNT, BREW_RANGE, BREWING_XP_PER_UNIT, COOK_PER_DISH, COOK_PER_BREW,
  PAN_ARROWS_BASE, PAN_ARROWS_PER_TIER, PAN_GAP_BASE, PAN_GAP_MIN, PAN_SPEED_RAMP,
  PAN_HIT_WINDOW, PAN_MISS_BURN, PAN_HIT_RELIEF, PAN_INPUT_SLACK, PAN_DEADLINE_PAD,
  COOK_TIER, COOK_TIER_DEFAULT, FIRE_BURN_SECONDS, FIRE_COUNT, FIRE_RANGE,
} from "./sim/cooking.ts";
import {
  CHOP_DEFAULTS, type ChopTuning,
} from "./sim/chopping.ts";
import {
  FIRE_DEFAULTS, type FireStartTuning,
} from "./sim/firestarting.ts";

export type FishingTunables = {
  castRange: number;
  castCd: number;
  // the encounter (notice → approach → investigate → nibble → bite)
  noticeRadius: number;
  noticeEvery: number;
  investigateMin: number;
  investigateMax: number;
  nibbleMin: number;
  nibbleMax: number;
  nibbleDur: number;
  nibbleGapMin: number;
  nibbleGapMax: number;
  nibbleSpook: number;
  pullSpook: number;
  biteWindow: number;
  spookCd: number;
  loseCd: number;
  waryBump: number;
  waryDecay: number;
  // population
  fishRespawn: number;
  shadowDepthOffset: number;
  // the reel minigame
  reelArrowsBase: number;
  reelArrowsPerTier: number;
  reelGapBase: number;
  reelGapMin: number;
  reelSpeedRamp: number;
  reelHitWindow: number;
  reelMissTension: number;
  reelHitRelief: number;
  reelInputSlack: number;
  reelDeadlinePad: number;
  // netting (untouched by the rework)
  soakTime: number;
  netCapacityMin: number;
  netCapacityMax: number;
  netCount: number;
  netRange: number;
  anglingXp: Record<string, number>;
  anglingXpDefault: number;
  nettingXpPerFish: number;
  fishmongerPerCatch: number;
  fishmongerPerHaul: number;
};

const fishingDoc = loadedTunables.find((t) => t.id === "fishing");

/** The effective fishing numbers: content/tunables/fishing.json field-by-field
 *  over the sim defaults. Rod/net item blocks still override per item
 *  (fishing.castRange, net.soakTime), fish blocks per species, and box grants
 *  still fold on top (fishingSum) — this is only the BASE layer those start
 *  from. FISHING_TUNING is structurally an EncounterTuning + ReelTuning, so
 *  callers pass it straight into the sim machines. */
export const FISHING_TUNING: FishingTunables = {
  castRange: fishingDoc?.castRange ?? CAST_RANGE,
  castCd: fishingDoc?.castCd ?? CAST_CD,
  noticeRadius: fishingDoc?.noticeRadius ?? NOTICE_RADIUS,
  noticeEvery: fishingDoc?.noticeEvery ?? NOTICE_EVERY,
  investigateMin: fishingDoc?.investigateMin ?? INVESTIGATE_MIN,
  investigateMax: fishingDoc?.investigateMax ?? INVESTIGATE_MAX,
  nibbleMin: fishingDoc?.nibbleMin ?? NIBBLE_MIN,
  nibbleMax: fishingDoc?.nibbleMax ?? NIBBLE_MAX,
  nibbleDur: fishingDoc?.nibbleDur ?? NIBBLE_DUR,
  nibbleGapMin: fishingDoc?.nibbleGapMin ?? NIBBLE_GAP_MIN,
  nibbleGapMax: fishingDoc?.nibbleGapMax ?? NIBBLE_GAP_MAX,
  nibbleSpook: fishingDoc?.nibbleSpook ?? NIBBLE_SPOOK,
  pullSpook: fishingDoc?.pullSpook ?? PULL_SPOOK,
  biteWindow: fishingDoc?.biteWindow ?? BITE_WINDOW,
  spookCd: fishingDoc?.spookCd ?? SPOOK_CD,
  loseCd: fishingDoc?.loseCd ?? LOSE_CD,
  waryBump: fishingDoc?.waryBump ?? WARY_BUMP,
  waryDecay: fishingDoc?.waryDecay ?? WARY_DECAY,
  fishRespawn: fishingDoc?.fishRespawn ?? FISH_RESPAWN,
  shadowDepthOffset: fishingDoc?.shadowDepthOffset ?? SHADOW_DEPTH_OFFSET,
  reelArrowsBase: fishingDoc?.reelArrowsBase ?? REEL_ARROWS_BASE,
  reelArrowsPerTier: fishingDoc?.reelArrowsPerTier ?? REEL_ARROWS_PER_TIER,
  reelGapBase: fishingDoc?.reelGapBase ?? REEL_GAP_BASE,
  reelGapMin: fishingDoc?.reelGapMin ?? REEL_GAP_MIN,
  reelSpeedRamp: fishingDoc?.reelSpeedRamp ?? REEL_SPEED_RAMP,
  reelHitWindow: fishingDoc?.reelHitWindow ?? REEL_HIT_WINDOW,
  reelMissTension: fishingDoc?.reelMissTension ?? REEL_MISS_TENSION,
  reelHitRelief: fishingDoc?.reelHitRelief ?? REEL_HIT_RELIEF,
  reelInputSlack: fishingDoc?.reelInputSlack ?? REEL_INPUT_SLACK,
  reelDeadlinePad: fishingDoc?.reelDeadlinePad ?? REEL_DEADLINE_PAD,
  soakTime: fishingDoc?.soakTime ?? SOAK_TIME,
  netCapacityMin: fishingDoc?.netCapacityMin ?? NET_CAPACITY_MIN,
  netCapacityMax: fishingDoc?.netCapacityMax ?? NET_CAPACITY_MAX,
  netCount: fishingDoc?.netCount ?? NET_COUNT,
  netRange: fishingDoc?.netRange ?? NET_RANGE,
  anglingXp: fishingDoc?.anglingXp ?? ANGLING_XP,
  anglingXpDefault: fishingDoc?.anglingXpDefault ?? ANGLING_XP_DEFAULT,
  nettingXpPerFish: fishingDoc?.nettingXpPerFish ?? NETTING_XP_PER_FISH,
  fishmongerPerCatch: fishingDoc?.fishmongerPerCatch ?? FISHMONGER_PER_CATCH,
  fishmongerPerHaul: fishingDoc?.fishmongerPerHaul ?? FISHMONGER_PER_HAUL,
};

export type HuntingTunables = {
  trapSoak: number;
  trapCapacityMin: number;
  trapCapacityMax: number;
  trapCount: number;
  trapRange: number;
  trophyTierMul: number;
  stalkingXp: Record<string, number>;
  stalkingXpDefault: number;
  trappingXpPerGame: number;
  huntsmanPerTake: number;
  huntsmanPerHaul: number;
  huntsmanPerTrophy: number;
};

const huntingDoc = loadedTunables.find((t) => t.id === "hunting");

/** The effective hunting numbers: content/tunables/hunting.json field-by-field
 *  over the sim defaults. Bow/trap item blocks still override per item
 *  (trap.soakTime/capacity, the bow's bands + hunting.aimHitBonus), and box
 *  grants still fold on top (huntingSum) — this is only the BASE layer. */
export const HUNTING_TUNING: HuntingTunables = {
  trapSoak: huntingDoc?.trapSoak ?? TRAP_SOAK,
  trapCapacityMin: huntingDoc?.trapCapacityMin ?? TRAP_CAPACITY_MIN,
  trapCapacityMax: huntingDoc?.trapCapacityMax ?? TRAP_CAPACITY_MAX,
  trapCount: huntingDoc?.trapCount ?? TRAP_COUNT,
  trapRange: huntingDoc?.trapRange ?? TRAP_RANGE,
  trophyTierMul: huntingDoc?.trophyTierMul ?? TROPHY_TIER_MUL,
  stalkingXp: huntingDoc?.stalkingXp ?? STALKING_XP,
  stalkingXpDefault: huntingDoc?.stalkingXpDefault ?? STALKING_XP_DEFAULT,
  trappingXpPerGame: huntingDoc?.trappingXpPerGame ?? TRAPPING_XP_PER_GAME,
  huntsmanPerTake: huntingDoc?.huntsmanPerTake ?? HUNTSMAN_PER_TAKE,
  huntsmanPerHaul: huntingDoc?.huntsmanPerHaul ?? HUNTSMAN_PER_HAUL,
  huntsmanPerTrophy: huntingDoc?.huntsmanPerTrophy ?? HUNTSMAN_PER_TROPHY,
};

export type CookingTunables = {
  hearthRange: number;
  cookCd: number;
  cookXp: Record<string, number>;
  cookXpDefault: number;
  panArrowsBase: number;
  panArrowsPerTier: number;
  panGapBase: number;
  panGapMin: number;
  panSpeedRamp: number;
  panHitWindow: number;
  panMissBurn: number;
  panHitRelief: number;
  panInputSlack: number;
  panDeadlinePad: number;
  cookTier: Record<string, number>;
  cookTierDefault: number;
  fireBurnSeconds: number;
  fireCount: number;
  fireRange: number;
  brewSoak: number;
  brewCapacityMin: number;
  brewCapacityMax: number;
  brewCount: number;
  brewRange: number;
  brewingXpPerUnit: number;
  cookPerDish: number;
  cookPerBrew: number;
};

const cookingDoc = loadedTunables.find((t) => t.id === "cooking");

/** The effective cooking numbers: content/tunables/cooking.json field-by-field
 *  over the sim defaults. Box grants still fold on top (cookingSum) — this is
 *  only the BASE layer those start from. cookTier is per-ingredient (an absent
 *  ingredient uses cookTierDefault); the studio-first authoring loop tunes it in
 *  the Skill-Sim and Applies it back here. */
export const COOKING_TUNING: CookingTunables = {
  hearthRange: cookingDoc?.hearthRange ?? HEARTH_RANGE,
  cookCd: cookingDoc?.cookCd ?? COOK_RATE_CD,
  cookXp: cookingDoc?.cookXp ?? COOK_XP,
  cookXpDefault: cookingDoc?.cookXpDefault ?? COOK_XP_DEFAULT,
  panArrowsBase: cookingDoc?.panArrowsBase ?? PAN_ARROWS_BASE,
  panArrowsPerTier: cookingDoc?.panArrowsPerTier ?? PAN_ARROWS_PER_TIER,
  panGapBase: cookingDoc?.panGapBase ?? PAN_GAP_BASE,
  panGapMin: cookingDoc?.panGapMin ?? PAN_GAP_MIN,
  panSpeedRamp: cookingDoc?.panSpeedRamp ?? PAN_SPEED_RAMP,
  panHitWindow: cookingDoc?.panHitWindow ?? PAN_HIT_WINDOW,
  panMissBurn: cookingDoc?.panMissBurn ?? PAN_MISS_BURN,
  panHitRelief: cookingDoc?.panHitRelief ?? PAN_HIT_RELIEF,
  panInputSlack: cookingDoc?.panInputSlack ?? PAN_INPUT_SLACK,
  panDeadlinePad: cookingDoc?.panDeadlinePad ?? PAN_DEADLINE_PAD,
  cookTier: cookingDoc?.cookTier ?? COOK_TIER,
  cookTierDefault: cookingDoc?.cookTierDefault ?? COOK_TIER_DEFAULT,
  fireBurnSeconds: cookingDoc?.fireBurnSeconds ?? FIRE_BURN_SECONDS,
  fireCount: cookingDoc?.fireCount ?? FIRE_COUNT,
  fireRange: cookingDoc?.fireRange ?? FIRE_RANGE,
  brewSoak: cookingDoc?.brewSoak ?? BREW_SOAK,
  brewCapacityMin: cookingDoc?.brewCapacityMin ?? BREW_CAPACITY_MIN,
  brewCapacityMax: cookingDoc?.brewCapacityMax ?? BREW_CAPACITY_MAX,
  brewCount: cookingDoc?.brewCount ?? BREW_COUNT,
  brewRange: cookingDoc?.brewRange ?? BREW_RANGE,
  brewingXpPerUnit: cookingDoc?.brewingXpPerUnit ?? BREWING_XP_PER_UNIT,
  cookPerDish: cookingDoc?.cookPerDish ?? COOK_PER_DISH,
  cookPerBrew: cookingDoc?.cookPerBrew ?? COOK_PER_BREW,
};

const choppingDoc = loadedTunables.find((t) => t.id === "chopping");

/** Effective tree-cutting numbers: content/tunables/chopping.json over sim defaults. */
export const CHOPPING_TUNING: ChopTuning = {
  promptCount: choppingDoc?.promptCount ?? CHOP_DEFAULTS.promptCount,
  gap: choppingDoc?.gap ?? CHOP_DEFAULTS.gap,
  gapMin: choppingDoc?.gapMin ?? CHOP_DEFAULTS.gapMin,
  hitWindow: choppingDoc?.hitWindow ?? CHOP_DEFAULTS.hitWindow,
  missTension: choppingDoc?.missTension ?? CHOP_DEFAULTS.missTension,
  hitRelief: choppingDoc?.hitRelief ?? CHOP_DEFAULTS.hitRelief,
  chopStaCost: choppingDoc?.chopStaCost ?? CHOP_DEFAULTS.chopStaCost,
  inputSlack: choppingDoc?.inputSlack ?? CHOP_DEFAULTS.inputSlack,
  deadlinePad: choppingDoc?.deadlinePad ?? CHOP_DEFAULTS.deadlinePad,
  sessionTimeout: choppingDoc?.sessionTimeout ?? CHOP_DEFAULTS.sessionTimeout,
};

const firestartDoc = loadedTunables.find((t) => t.id === "firestarting");

/** Effective firestarting numbers: content/tunables/firestarting.json over sim defaults.
 *  Cost is materials (kindling + log) on success — no stamina. */
export const FIRESTARTING_TUNING: FireStartTuning = {
  promptCount: firestartDoc?.promptCount ?? FIRE_DEFAULTS.promptCount,
  gap: firestartDoc?.gap ?? FIRE_DEFAULTS.gap,
  gapMin: firestartDoc?.gapMin ?? FIRE_DEFAULTS.gapMin,
  hitWindow: firestartDoc?.hitWindow ?? FIRE_DEFAULTS.hitWindow,
  missTension: firestartDoc?.missTension ?? FIRE_DEFAULTS.missTension,
  hitRelief: firestartDoc?.hitRelief ?? FIRE_DEFAULTS.hitRelief,
  inputSlack: firestartDoc?.inputSlack ?? FIRE_DEFAULTS.inputSlack,
  deadlinePad: firestartDoc?.deadlinePad ?? FIRE_DEFAULTS.deadlinePad,
  sessionTimeout: firestartDoc?.sessionTimeout ?? FIRE_DEFAULTS.sessionTimeout,
};
